<script>
export const pageName = 'rockets-flight-edit';
export const pageRoute = '/rockets/{rocketId}/flights/{flightId}/edit';
export const title = 'Edit Flight'
export const requireMembership = true;

import { hideLoading, showLoading } from '../../js/utils/loading.js';
import { navigate } from '@trullock/page-manager'
import { commands, queries } from '../../js/lib/functions.js';
import { showToast } from '../../js/utils/toast.js'
import DateTime from '../../../functions/lib/dateTime.js';
import TomSelect from 'tom-select'
import { getMapTasksForFlightLog, getUserMap } from '../../js/utils/map.js';
import { getCurrentUserId } from '../../js/auth.js';
import { listenToCurrentUserView, listenToMotorsInventory, listenToRocketView } from '../../js/readModel.js';
import { attemptTypeToFullAttempt } from '../../../functions/domain/certification-utils.js';
import { cropImage } from '../../js/utils/cropper.js';

export default {
	data() {
		return { 
			user: null,
			rocket: null,
			flight: null,
			date: null,
			dateMax: DateTime.now.format('yyyy-MM-dd'),
			time: null,
			name: null,
			location: null,
			launchLatitude: null,
			launchLongitude: null,
			landingLatitude: null,
			landingLongitude: null,
			motors: null,
			ejections: null,
			mass: null,
			sm: null,
			predictedAlt: null,
			notes: null,
			outcome: '',
			mapTasks: [],
			flightLogs: [],
			flightLogError: null,
			mapFlight: false,
			availableMapTasks: [],
			certFlight: false,
			certFlightType: null,
			existingLogs: [],
			flightCard: null,
			isDirty: false,
			attemptPending: false,
			recordAttempt: false,
			altimeter: null,
			teamName: null,
			reachedAlt: null,
			recordType: 'single',
			recordClass: 'A',
			witness: null,
			eligibleForCertification: false,
			ready: false
		}
	},

	methods: {
		beforeHide() {
			return this.isDirty ? 'Are you sure you want to abandon your changes?' : null
		},
		async boot(opts) {
			let self = this;

			this.$mapTasks = new TomSelect(this.$refs.mapTasks, {
				options: [],
				render: {
					option: function (data, escape) {
						return `<div><div class="ts-map-icon" style="background: url(${data.bg})"><img src="${data.icon}"></div>${data.text}</div>`;
					},
					item: function (item, escape) {
						return `<div><div class="ts-map-icon" style="background: url(${item.bg})"><img src="${item.icon}"></div>${item.text}</div>`;
					}
				},
				onItemAdd: function(){
					this.setTextboxValue('');
					this.clearOptions();

					let subjects = this.items.map(i => i.split('.')[0]);

					let newOptions = self.availableMapTasks.filter(t => subjects.indexOf(t.subjectId) == -1 || t.subjectId == 'bonus')
					this.addOptions(newOptions);

					this.refreshOptions();
				},
				onItemRemove: function(){
					this.setTextboxValue('');
					this.clearOptions();

					let subjects = this.items.map(i => i.split('.')[0]);

					let newOptions = self.availableMapTasks.filter(t => subjects.indexOf(t.subjectId) == -1 || t.subjectId == 'bonus')
					this.addOptions(newOptions);

					this.refreshOptions();
				},
				maxItems: null,
				create: false,
				refreshThrottle: 1,
				plugins: {
					remove_button:{
						title: 'Remove this item',
					}
				}
			});

			this.$motors = new TomSelect(this.$refs.motors,{
				options: [],
				render: {
					option: function (data, escape) {
						if(data.id && data.text)
							return `<div>${escape(data.text)}</div>`;
						return `<div><span class="fe fe-box mr-2"></span> ${escape(data.manufacturer + ' ' + data.designation + '-' + data.delay)} (x<span class="js-qty">${data.quantity}</span></a>)</div>`;
					},
					item: function (item, escape) {
						if(item.id && item.text)
							return `<div>${escape(item.text)}</div>`;

						return `<div><span class="fe fe-box mr-2"></span> ${escape(item.manufacturer + ' ' + item.designation + '-' + item.delay)}</div>`;
					}
				},
				searchField: ['name', 'designation', 'manufacturer'],
				valueField: 'id',
				maxItems: null,
				create: true,
				onItemAdd: id => {
					if(this.motorQuantityAdjust === false)
						return;

					let motor = this.$motors.options[id];
					// creations
					if(motor.quantity === undefined)
						return;
					motor.$div.querySelector('.js-qty').textContent = --motor.quantity
					if(motor.quantity <= 0)
					{
						motor.$div.removeAttribute('data-selectable')
						this.$motors.setActiveOption(null);
					}

					self.ejections = (self.ejections?.length ? self.ejections + ', ' : '') + motor.delay;
				},
				onItemRemove: id => {
					if(this.motorQuantityAdjust === false)
						return;
					
					let motor = this.$motors.options[id];
					// creations
					if(motor?.quantity === undefined)
						return;

					// handle clear()
					if(!motor.$div)
						return;

					motor.$div.querySelector('.js-qty').textContent = ++motor.quantity
					if(motor.quantity > 0)
						motor.$div.setAttribute('data-selectable', '')

					let reg = new RegExp(`(^${motor.delay}s?,\\s?)|(,\\s?${motor.delay}s?)|(^${motor.delay}s?$)`);
					self.ejections = (self.ejections || '').replace(reg, '')
				},
				persist: false,
				refreshThrottle: 1,
				duplicates: true,
				hideSelected: false,
				plugins: {
					remove_button:{
						title: 'Remove this item',
					}
				}
			});

			await listenToRocketView(getCurrentUserId(), opts.rocketId, rocket => {
				this.rocket = rocket
				this.flight = this.rocket.getFlight(opts.flightId);
			});

			await listenToCurrentUserView(async user => {
				this.user = user;
				this.attemptPending = this.user.map.attemptPending || this.user.certification.attemptPending

				let map = await getUserMap(this.user);
				this.availableMapTasks = getMapTasksForFlightLog(map);
				this.$mapTasks.clear();
				this.$mapTasks.clearOptions();
				this.$mapTasks.addOptions(this.availableMapTasks);

				this.eligibleForCertification = this.user.eligibleForCertification;
			});
			
			await listenToMotorsInventory(getCurrentUserId(), 'stock', inventory => {
				
				let motors = [...inventory.motors]
				let options = motors.map(m => ({
					id: m.motorId,
					name: m.name,
					designation: m.designation,
					manufacturer: m.manufacturer,
					delay: m.delay,
					quantity: m.quantity,
					disabled: m.quantity <= 0
				}))
				
				this.$motors.clear();
				this.$motors.clearOptions();
				this.$motors.addOptions(options);
			});

			this.ready = true;
		},
		async show(opts) {
			commands.warmUp();

			this.date = this.flight.flownOn.format('yyyy-MM-dd');
			this.time = this.flight.flownOn.format('HH:mm');

			this.name = this.flight.name;
			this.location = this.flight.location;
			this.launchLatitude = this.flight.latitude;
			this.launchLongitude = this.flight.longitude;
			this.landingLatitude = this.flight.landingLatitude;
			this.landingLongitude = this.flight.landingLongitude;
			this.motors = this.flight.motors;
			
			this.$motors.clear();
			this.motorQuantityAdjust = false;
			
			this.flight.motors.forEach(m => {
				this.$motors.addItem(m.inventoryId, true)
			})
			this.motorQuantityAdjust = true;

			this.ejections = this.flight.ejections;
			this.mass = this.flight.mass;
			this.sm = this.flight.sm;
			this.predictedAlt = this.flight.predictedAlt;
			this.notes = this.flight.notes;
			this.outcome = this.flight.outcome;

			this.flightLogs = [];
			this.existingLogs = this.flight.logs.map(l => ({
				id: l.id,
				name: l.name,
				size: l.size,
				remove: false
			}))

			this.mapFlight = false;
			this.certFlight = false;
			this.certFlightType = attemptTypeToFullAttempt(this.user.certification.nextStage)
			this.flightCard = null;
			this.mapTasks = [];
			this.$mapTasks.clear();
			this.$refs.fupFlightCard.value = null;
			this.$refs.lblFlightCard.innerHTML = "Choose image&hellip;";
			
			this.recordAttempt = false;
			this.altimeter = null
			this.teamName = null
			this.recordType = 'single';
			this.recordClass = 'A'
			this.reachedAlt = null
			this.witness = null

			this.$refs.outcome.setCustomValidity('');
			this.$refs.fupFlightCard.setCustomValidity('');
			this.$refs.fupFlightLogs.setCustomValidity('');
			this.$refs.mapTasks.setCustomValidity('');
			
			this.isDirty = false;
		},
		geolocateLaunch() {
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(async position => {
					this.launchLatitude = parseFloat(position.coords.latitude.toFixed(7));
					this.launchLongitude = parseFloat(position.coords.longitude.toFixed(7));
				}, e => {
					console.error(e)
					showToast({
						message: 'Error geolocating you',
						style: 'bg-danger'
					})
				});
			} else {
				showToast({
					message: 'Error geolocating you',
					style: 'bg-danger'
				})
			}
		},
		geolocateLanding() {
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(async position => {
					this.landingLatitude = parseFloat(position.coords.latitude.toFixed(7));
					this.landingLongitude = parseFloat(position.coords.longitude.toFixed(7));
				});
			} else {
				showToast({
					message: 'Error geolocating you',
					style: 'bg-danger'
				})
			}
		},
		changed() {
			this.isDirty = true;
			this.mapFlight ? this.$mapTasks.enable() : this.$mapTasks.disable()
		},
		flightLogAdded(e) {
			var files = [...e.target.files];
			this.handleFiles(files);
		},
		handleFiles(files) {
			
			this.flightLogError = '';

			let filesProcessed = 0;

			var done = async (log, file) => {
				
				filesProcessed++;

				this.$refs.fupFlightLogs.value = null;

				let result = await queries.rockets.flightLogValidate({ log })
				
				if(filesProcessed == files.length)
					hideLoading();

				if(!result.valid)
				{
					this.flightLogError += `Unsupported flight log '${file.name}'. If this is a valid flight log file please email it to membership@ukra.org.uk so we can investigate supporting it.`;
					return;
				}

				if(file.size > 5 * 1024 * 1024)
				{	
					this.flightLogError += `'${file.name}' is too large, maximum log size is 5MB.`;
					return;
				}

				this.flightLogs.push({ 
					log, 
					name: file.name,
					size: file.size
				});

				this.$refs.fupFlightLogs.value = null;
			};

			let error = (result, e) => {
				filesProcessed++;
				if(filesProcessed == files.length)
					hideLoading();

				this.$refs.fupFlightLogs.setCustomValidity(e);
				this.$refs.fupFlightLogs.reportValidity();
			}

			showLoading();
			
			files.forEach(f => {
				let reader = new FileReader();
				reader.onload = () => done(reader.result, f);
				reader.onerror = e => error(reader.result, e);

				if(f.type == 'application/octet-stream')
				{
					reader.readAsDataURL(f);
				}
				else if(f.type == 'text/csv')
				{
					reader.readAsText(f);
				}
				else
				{
					error(null, 'Unsupported file type')
				}
			});
		},
		removeLog(log) {
			this.flightLogs.splice(this.flightLogs.findIndex(l => l == log), 1);
		},
		removeExistingLog(log) {
			log.remove = true;
		},
		dropped(e) {
			e.preventDefault();
			let files = [...e.dataTransfer.files];
			
			let dudFiles = files.filter(f => f.type != "text/csv");

			dudFiles.forEach(file => {
				this.$refs.fupFlightLogs.setCustomValidity(`Unsupported file type '${file.name}'.`);
				this.$refs.fupFlightLogs.reportValidity();
			})
			
			files = files.filter(f => f.type == "text/csv");

			if(files.length)
				this.handleFiles(files);
		},
		dragover(e) {
			e.preventDefault();
		},
		uploadClick(e) {
			this.$refs.fupFlightLogs.click();
		},
		async submit () {

			// we have to manually validate the file upload because `required` doesnt have good UX cross browser
			if((this.mapFlight || this.certFlight || this.recordAttempt) && !this.flightCard)
			{
				this.$refs.fupFlightCard.setCustomValidity('Please provide an picture of your completed flight card');
				this.$refs.fupFlightCard.reportValidity();
				return;
			}

			// we have to manually validate the file upload because `required` doesnt have good UX cross browser
			if((this.mapFlight || this.certFlight || this.recordAttempt) && this.outcome != 'positive')
			{
				this.$refs.outcome.setCustomValidity('Must have been a positive flight to pass MAP assessment');
				this.$refs.outcome.reportValidity();
				return;
			}

			// we have to manually validate the file upload because `required` doesnt have good UX cross browser
			if(this.recordAttempt)
			{
				let valid = true;
				
				if(this.flightLogs.length + this.existingLogs.length == 0)
				{
					valid = false;
					this.$refs.fupFlightLogs.setCustomValidity('You must provide flight logs');
					this.$refs.fupFlightLogs.reportValidity();
				}
				
				if(this.recordType == 'handicapped')
				{
					if(this.recordClass.charCodeAt(0) < 'F'.charCodeAt(0))
					{
						valid = false;
						this.$refs.recordClass.setCustomValidity('Only F and above are valid for Handicapped records');
						this.$refs.recordClass.reportValidity();
					}
				}

				if(!valid)
					return;
			}

			showLoading();

			this.flightLogError = null;

			let flownOn = `${this.date} ${this.time}`;
			flownOn = new DateTime(flownOn)

			let motors = this.$motors.items.map(i => this.$motors.options[i]).map(m => ({
				inventoryId: m.id == m.text ? '' : m.id,
				name: m.id == m.text ? m.text : m.manufacturer + ' ' + m.designation
			}));

			let tasks = []
			tasks.push(commands.rockets.flightUpdate({ 
				rocketId: this.rocket.id,
				flightId: this.flight.id,
				flownOn,
				name: this.name,
				location: this.location,
				launchLatitude: parseFloat(this.launchLatitude || '0') || null,
				launchLongitude: parseFloat(this.launchLongitude || '0') || null,
				motors: motors,
				ejections: this.ejections,
				mass: parseInt(this.mass || '0', 10) || null,
				sm: parseFloat(this.sm || '0') || null,
				predictedAlt: parseInt(this.predictedAlt || '0', 10) || null,
				landingLatitude: parseFloat(this.landingLatitude || '0') || null,
				landingLongitude: parseFloat(this.landingLongitude || '0') || null,
				outcome: this.outcome,
				mapFlight: this.mapFlight,
				mapTasks: this.mapTasks,
				certFlight: this.certFlight,
				flightCard: this.flightCard,
				notes: this.notes,
				recordAttempt: this.recordAttempt,
				altimeter: this.altimeter,
				teamName: this.teamName,
				reachedAlt: this.reachedAlt,
				recordType: this.recordType,
				recordClass: this.recordClass,
				witness: this.witness
			}));

			this.flightLogs.forEach(log => {
				tasks.push(commands.rockets.flightLogAttach({
					rocketId: this.rocket.id,
					flightId: this.flight.id,
					log: log.log,
					name: log.name	
				}))
			})

			this.existingLogs.filter(l => l.remove).forEach(l => {
				tasks.push(commands.rockets.flightLogRemove({
					rocketId: this.rocket.id,
					flightId: this.flight.id,
					logId: l.id
				}))
			})

			let results = await Promise.allSettled(tasks);
			
			if(results.filter(r => r.status == 'rejected').length != 0 || results.filter(r => r.value.success !== true).length != 0)
			{
				// TODO better error handling here
				showToast({ message: 'Error editing flight', style: 'bg-danger'})
				hideLoading();
				return;
			}

			this.isDirty = false;

			navigate(`/rockets/${this.rocket.id}`)

			showToast({
				message: 'Flight updated',
				style: 'bg-success'
			})

			hideLoading();
		},
		flightCardAdded(e) {
			this.$refs.fupFlightCard.setCustomValidity('');
			this.$refs.fupFlightCard.reportValidity();

			var files = e.target.files;

			if (files && files.length > 0) {
				let file = files[0];

				this.$refs.fupFlightCard.value = '';
				this.$refs.fupFlightCard.blobData = null;
				
				cropImage({
					viewMode: 1,
					autoCropArea: 1
				}, { 
					width: 1024, 
					height: 1024 
				}, file).then(canvas => {
					this.flightCard = canvas.toDataURL();

					canvas.toBlob(blob => {
						this.$refs.fupFlightCard.blobData = {
							blob: blob,
							name: 'flight-card.jpg'
						};
						
						this.$refs.lblFlightCard.innerText = file.name;
					});
				}, e => {
					if(e instanceof Error)
					{
						this.$refs.fupFlightCard.setCustomValidity(e.message);
						this.$refs.fupFlightCard.reportValidity();
					}
				})
			}
		},
		certFlightChanged() {
			if(this.certFlight)
				this.mapFlight = false;
		},
		mapFlightChanged() {
			if(this.mapFlight)
				this.certFlight = false;
		}
	}
}
</script>
<template>
	<div v-show="ready" class="container py-5">
		<div v-show="flight?.locked" class="row justify-content-center">
			<div class="col-12 col-md-8 col-xl-6 my-5">
				<h1 class="display-4 text-center mb-3">Edit Flight</h1>
				<p class="text-muted text-center mb-5">Update a previous flight.</p>

				<div class="alert alert-light">
					<span class="fe fe-info"></span> This flight cannot be edited as is it part of a MAP or Certification attempt.
				</div>
				<a :href="`/rockets/${rocket?.id}`" class="btn btn-primary btn-block">Back to rocket <span class="fe fe-arrow-left-circle"></span></a>
			</div>
		</div>
		<div v-show="!flight?.locked" class="row justify-content-center">
			<div class="col-12 col-md-8 col-xl-6 my-5">
				<h1 class="display-4 text-center mb-3">Edit Flight</h1>
				<p class="text-muted text-center mb-5">Update a previous flight.</p>
					
				<form @submit.prevent="submit" @change="changed">
					<h2>Flight</h2>
					<div class="form-group">
						<label class="form-label">Description</label>
						<input type="text" class="form-control" v-model="name" name="flight-desc" placeholder="e.g. First flight" autofocus>
						<span class="invalid-feedback"></span>
						<p class="form-text text-muted small mb-0">Optional. A unique description for this flight.</p>
					</div>
					<div class="row">
						<div class="col-6">
							<div class="form-group">
								<label class="form-label">Date</label>
								<input type="date" class="form-control pe-3" v-model="date" :max="dateMax" :required="mapFlight || certFlight || recordAttempt">
								<span class="invalid-feedback"></span>
							</div>
						</div>
						<div class="col-6">
							<div class="form-group">
								<label class="form-label">Time</label>
								<input type="time" class="form-control pe-3" v-model="time" :required="mapFlight || certFlight || recordAttempt">
								<span class="invalid-feedback"></span>
							</div>
						</div>
					</div>
					<hr class="mt-2 mb-4" />
					<h2>Launch site</h2>
					<div class="form-group">
						<label class="form-label">Name</label>
						<input type="text" class="form-control" v-model="location" :required="recordAttempt" placeholder="e.g. Cape Canaveral">
						<span class="invalid-feedback"></span>
					</div>
					<div class="row mb-2">
						<div class="col">
							<div class="form-group mb-0">
								<label class="form-label">Latitude</label>
								<input type="number" class="form-control" v-model="launchLatitude" step="0.0000001">
								<span class="invalid-feedback"></span>
							</div>
						</div>
						<div class="col">
							<div class="form-group mb-0">
								<label class="form-label">Longitude</label>
								<input type="number" class="form-control" v-model="launchLongitude" step="0.0000001">
								<span class="invalid-feedback"></span>
							</div>
						</div>
						<div class="col-auto d-flex align-items-end pt-4 ps-0">
							<button @click.prevent="geolocateLaunch" class="btn btn-outline-secondary"><span class="fe fe-map-pin"></span></button>
						</div>
					</div>
					
					<div class="form-text text-muted small mb-4">Use the Geolocate button in the field for exact coordinates.</div>

					<hr class="mt-2 mb-4"/>
					<h2>Configuration</h2>
					<div class="row">
						<div class="col-9">
							<div class="form-group">
								<label class="form-label">
									Motor(s)
								</label>
								<input type="text" class="form-control" ref="motors" v-model="motors" placeholder="e.g. CTI G80SK" :required="mapFlight || certFlight || recordAttempt">
								<span class="invalid-feedback"></span>
							</div>
						</div>
						<div class="col-3">
							<div class="form-group">
								<label class="form-label">
									Ejection(s)
								</label>
								<input type="text" autocomplete="off" v-model="ejections" class="form-control" placeholder="e.g. 7" :required="mapFlight || certFlight || recordAttempt" />
								<span class="invalid-feedback"></span>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col-6">
							<div class="form-group">
								<label class="form-label">
									Lift-off mass
								</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" :required="certFlight || recordAttempt" v-model="mass" class="form-control" placeholder="e.g. 1100" />
									<div class="input-group-append">
										<div class="input-group-text">
											g
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
						<div class="col-6">
							<div class="form-group">
								<label class="form-label">
									Stability margin
								</label>
								<input type="number" class="form-control" v-model="sm" min="0" max="100" step="0.1" :required="certFlight || recordAttempt" placeholder="e.g. 1.1">
								<span class="invalid-feedback"></span>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-12 col-md-6">
							<div class="form-group">
								<label>Predicted Altitude</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" :required="certFlight || recordAttempt" v-model="predictedAlt" class="form-control" placeholder="e.g. 3000" />
									<div class="input-group-append">
										<div class="input-group-text">
											feet
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
					</div>
					
					<div class="form-group">
						<label class="form-label mb-1">Notes</label>
						<textarea class="form-control" v-model="notes" rows="4"></textarea>
					</div>
					<hr class="my-5" />
					<h2 id="outcome">Outcome</h2>

					<div class="form-group">
						<label class="form-label">Flight result</label>
						<select :required="mapFlight || certFlight || recordAttempt" ref="outcome" v-model="outcome" class="form-control">
							<option value="">Not flown yet</option>
							<option value="positive">Positive</option>
							<option value="neutral">Neutral</option>
							<option value="negative">Negative</option>
						</select>
						<span class="invalid-feedback"></span>
						<span class="form-text text-muted small mb-4">Was the flight a success?</span>
					</div>
					<div class="row mb-2">
						<div class="col">
							<div class="form-group mb-0">
								<label class="form-label">Latitude</label>
								<input type="number" class="form-control" v-model="landingLatitude" step="0.0000001">
								<span class="invalid-feedback"></span>
							</div>
						</div>
						<div class="col">
							<div class="form-group mb-0">
								<label class="form-label">Longitude</label>
								<input type="number" class="form-control" v-model="landingLongitude" step="0.0000001">
								<span class="invalid-feedback"></span>
							</div>
						</div>
						<div class="col-auto d-flex align-items-end pt-4 ps-0">
							<button @click.prevent="geolocateLanding" class="btn btn-outline-secondary"><span class="fe fe-map-pin"></span></button>
						</div>
					</div>
					<div class="form-text text-muted small mb-4">Use the Geolocate button in the field for exact coordinates.</div>

					<div class="form-group">
						<label>Flight logs</label>
						<div class="form-control form-control-dropzone">
							<div ref="dzLogs" class="dropzone dropzone-multiple dz-clickable" @click="uploadClick" @drop="dropped" @dragover="dragover">
								<input type="file" @change="flightLogAdded" ref="fupFlightLogs" multiple />
								<div class="dz-default dz-message">
									<button class="dz-button" type="button"><span class="fe fe-plus"></span> Add flight log CSVs</button>
								</div>
							</div>
							<div v-if="flightLogError" class="text-danger small mt-2">{{ flightLogError }}</div>
							<div class="list-group list-group-flush pt-4 my-n3">
								<div v-for="log of flightLogs" class="list-group-item">
									<div class="row align-items-center">
										<div class="col ms-n2">
											<h4 class="fw-normal mb-1">
												{{ log.name }}
											</h4>
										</div>
										<div class="col-auto">
											<button class="text-danger" type="button" @click="removeLog(log)"><span class="fe fe-x"></span></button>
										</div>
									</div>
								</div>
								<template v-for="log of existingLogs">
									<div v-if="!log.remove" class="list-group-item">
										<div class="row align-items-center">
											<div class="col ms-n2">
												<h4 class="fw-normal mb-1">
													{{ log.name }}
												</h4>
											</div>
											<div class="col-auto">
												<button class="text-danger" type="button" @click="removeExistingLog(log)"><span class="fe fe-x"></span></button>
											</div>
										</div>
									</div>
								</template>
							</div>
						</div>
						<p class="form-text text-muted small mb-0">Upload data logs from your flight computer. <br />This feature is still under development, if your flight log isn't supported please <a href="mailto:membership@ukra.org.uk">send it to us</a>.</p>
					</div>
					<hr class="my-5" />
					<h2>MAP and Certification</h2>
					<div v-if="attemptPending" class="alert alert-light small mb-4"><span class="fe fe-info"></span> You currently have a pending submission, please wait for UKRA to approve it before submitting another.</div>
					<div v-if="!eligibleForCertification" class="alert alert-light small mb-4"><span class="fe fe-info"></span> You are not eligible for certification. You must be at least 12 years old.</div>
					<div class="row">
						<div class="col-auto">
							<div class="form-group mb-0">
								<div class="custom-control custom-switch">
									<input @change="mapFlightChanged" type="checkbox" class="custom-control-input" :value="true" v-model="mapFlight" :disabled="attemptPending">
									<label class="custom-control-label">This is a MAP flight</label>
								</div>
							</div>
						</div>
						<div class="col-auto">
							<div class="form-group mb-0">
								<div class="custom-control custom-switch">
									<input @change="certFlightChanged" type="checkbox" class="custom-control-input" :value="true" v-model="certFlight" :disabled="attemptPending || !eligibleForCertification">
									<label class="custom-control-label">This is a Certification flight</label>
								</div>
							</div>
						</div>
					</div>
										
					<div v-show="mapFlight" class="form-group mt-4">
						<label class="form-label">
							MAP objectives
						</label>
						<select name="map" v-model="mapTasks" ref="mapTasks" class="form-control" :disabled="!mapFlight" required></select>
						<span class="invalid-feedback mt-2"></span>
						<p class="form-text text-muted small mb-0">This must match the flight card.</p>
					</div>
					
					<div v-show="certFlight" class="form-group mt-4">
						<label class="form-label">
							Certification flight
						</label>
						<input type="text" disabled v-model="certFlightType" class="form-control" />
					</div>

					<hr class="my-5" />

					<h2>Record Attempt</h2>
					<div class="form-group">
						<div class="custom-control custom-switch">
							<input type="checkbox" class="custom-control-input" :value="true" v-model="recordAttempt">
							<label class="custom-control-label">This is an altitude record attempt</label>
						</div>
					</div>
					<div v-show="recordAttempt" class="form-group">
						<label class="form-label">Team name</label>
						<input type="text" class="form-control" v-model="teamName" placeholder="e.g. Thunderbirds">
						<span class="invalid-feedback"></span>
						<p class="form-text text-muted small mb-0">Only for teams attempting a record.</p>
					</div>
					<div v-show="recordAttempt" class="form-group">
						<label class="form-label">Record type</label>
						<select :required="recordAttempt" v-model="recordType" class="form-control">
							<option value="single">Single motor</option>
							<option value="cluster">Cluster</option>
							<option value="staged">Staged</option>
							<option value="handicapped">Handicapped</option>
						</select>
						<span class="invalid-feedback"></span>
						<span class="form-text text-muted small mb-4">The type of record you are attempting</span>
					</div>
					<div v-show="recordAttempt" class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">Record class</label>
								<select ref="recordClass" :required="recordAttempt" v-model="recordClass" class="form-control">
									<option value="A">A</option>
									<option value="B">B</option>
									<option value="C">C</option>
									<option value="D">D</option>
									<option value="E">E</option>
									<option value="F">F</option>
									<option value="G">G</option>
									<option value="H">H</option>
									<option value="I">I</option>
									<option value="J">J</option>
									<option value="K">K</option>
									<option value="L">L</option>
									<option value="M">M</option>
									<option value="N">N</option>
									<option value="O">O</option>
								</select>
								<span class="invalid-feedback"></span>
								<span class="form-text text-muted small mb-4">Total impulse class for the flight</span>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label>Altitude reached</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" :required="recordAttempt" v-model="reachedAlt" class="form-control" placeholder="e.g. 3000" />
									<div class="input-group-append">
										<div class="input-group-text">
											feet
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
								<span class="form-text text-muted small mb-4">This must be AGL</span>
							</div>
						</div>
					</div>
					
					<div v-show="recordAttempt" class="form-group">
						<label class="form-label">Altimeter</label>
						<input type="text" class="form-control" v-model="altimeter" :required="recordAttempt" placeholder="e.g. Trullock Aerospace Gagarin">
						<span class="invalid-feedback"></span>
						<p class="form-text text-muted small mb-0">Provide the altimeter make and model</p>
					</div>

					<div v-show="recordAttempt" class="form-group">
						<label class="form-label">Witness</label>
						<input type="text" class="form-control" v-model="witness" :required="recordAttempt" placeholder="e.g. Tim Peake #1234">
						<span class="invalid-feedback"></span>
						<p class="form-text text-muted small mb-0">Provide the name and UKRA number or contact details of your witness</p>
					</div>

					<div v-show="mapFlight || certFlight || recordAttempt">
						<hr class="my-5" />
						<div class="field form-group">
							<label class="d-block">
								Upload a picture of the signed flight card
								<div class="row justify-content-center my-3">
									<div class="col">
										<div class="dz-default dz-message py-4" v-if="flightCard">
											<div class="avatar avatar-xxl">
												<img ref="imgAvatar" class="avatar-img" :src="flightCard" alt="Flight Card">
											</div>
										</div>
									</div>
								</div>
							</label>
							<div class="custom-file">
								<input type="file" @change="flightCardAdded" :disabled="!mapFlight && !certFlight && !recordAttempt" ref="fupFlightCard" class="custom-file-input" name="flight-card" accept="image/*;capture=camera">
								<label ref="lblFlightCard" class="custom-file-label text-muted">Choose image&hellip;</label>
								<span class="invalid-feedback"></span>
							</div>
							<span class="form-text text-muted small mb-0">Must be a jpeg or png.</span>
						</div>
					</div>
					

					<hr class="my-5" />

					<button class="btn w-100 btn-primary" v-if="!(mapFlight || certFlight || recordAttempt)">Save changes <span class="fe fe-check-circle"></span></button>
					<div class="alert alert-light" v-if="mapFlight || certFlight || recordAttempt"><span class="fe fe-info"></span> This flight will not be editable after submitting.</div>
					<button class="btn w-100 btn-success" v-if="mapFlight || certFlight || recordAttempt">Submit flight <span class="fe fe-check-circle"></span></button>
				</form>
			</div>
		</div>
    </div>
    
	<div class="modal fade" ref="cropper" tabindex="-1" role="dialog" aria-labelledby="modalLabel" aria-hidden="true">
		<div class="modal-dialog" role="document">
			<div class="modal-content">
				<div class="card mb-0">
					<div class="card-header">
						<h4 class="card-header-title">Crop the image</h4>
						<button type="button" class="close" @click.prevent="hideModal" aria-label="Close">
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
					<div class="card-body">
						<p>Your picture must be good quality and clearly legible.</p>
						<div class="img-container">
							<img ref="imgCropper" :src="flightCard" class="">
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" @click.prevent="hideModal">Cancel</button>
						<button type="button" class="btn btn-primary" @click.prevent="crop">Crop</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
